<template>
  <div class="video-session-page">

    <!-- Loading state -->
    <div v-if="state.loading" class="state-screen">
      <v-progress-circular indeterminate color="primary" size="64" />
      <p class="mt-4 text-medium-emphasis">Loading your session…</p>
    </div>

    <!-- Error state -->
    <div v-else-if="state.fatalError" class="state-screen">
      <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor" class="icon-error"><path d="M11 15h2v2h-2v-2zm0-8h2v6h-2V7zm1-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/></svg>
      <h3 class="mt-3">Unable to load session</h3>
      <p class="text-medium-emphasis mt-1">{{ state.fatalError }}</p>
      <v-btn class="mt-4" color="primary" variant="outlined" @click="$router.back()">Go back</v-btn>
    </div>

    <!-- Time-gate: too early -->
    <div v-else-if="state.gateStatus === 'too-early'" class="state-screen">
      <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor" class="icon-primary"><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23L12.5 13V7z"/></svg>
      <h3 class="mt-3 font-size-18-600">Your session isn't open yet</h3>
      <p class="text-medium-emphasis mt-2 text-center">
        This video room opens <strong>{{ formatDate(state.windowOpens) }}</strong>
        (5 minutes before your appointment).
      </p>
      <div class="countdown mt-4">
        <span class="countdown__label">Opens in</span>
        <span class="countdown__time">{{ state.countdown }}</span>
      </div>
      <v-btn class="mt-6" color="primary" variant="outlined" @click="$router.back()">Go back</v-btn>
    </div>

    <!-- Time-gate: too late -->
    <div v-else-if="state.gateStatus === 'too-late'" class="state-screen">
      <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor" class="icon-warning"><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-.5-5h1v2h-1v-2zm0-8h1v6h-1V7z"/></svg>
      <h3 class="mt-3 font-size-18-600">This session has ended</h3>
      <p class="text-medium-emphasis mt-2 text-center">
        The video room for this appointment is no longer available.
      </p>
      <v-btn class="mt-6" color="primary" variant="outlined" @click="$router.back()">Go back</v-btn>
    </div>

    <!-- Active: lobby (not yet joined) -->
    <div v-else-if="state.gateStatus === 'open' && !state.isConnected" class="lobby">
      <div class="lobby-card">
        <svg width="56" height="56" viewBox="0 0 24 24" fill="currentColor" class="icon-primary mb-3"><path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/></svg>
        <h2 class="font-size-18-600 mb-1">{{ state.event?.name ?? 'Video Session' }}</h2>
        <p v-if="state.event" class="text-medium-emphasis text-sm mb-4">
          {{ formatDate(state.event.start_date) }}
        </p>

        <!-- Camera/mic preview -->
        <div class="preview-container mb-4" ref="previewContainer">
          <div class="preview-placeholder">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" style="color:rgba(255,255,255,0.5)"><path d="M12 15.2A3.2 3.2 0 0 1 8.8 12 3.2 3.2 0 0 1 12 8.8 3.2 3.2 0 0 1 15.2 12 3.2 3.2 0 0 1 12 15.2M9 2 7.17 4H4C2.9 4 2 4.9 2 6v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9z"/></svg>
            <p class="mt-1" style="font-size:12px;color:#aaa">Camera preview</p>
          </div>
        </div>

        <p v-if="state.error" class="text-error mb-3">{{ state.error }}</p>

        <v-btn
          color="primary"
          size="large"
          rounded
          :loading="state.joining"
          @click="joinRoom"
        >
          <template #prepend>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/></svg>
          </template>
          Join Session
        </v-btn>
      </div>
    </div>

    <!-- Active: connected room -->
    <div v-else-if="state.isConnected" class="room-layout">

      <!-- Participants -->
      <div class="participants-grid" :class="{ 'single': state.participants.length <= 1 }">
        <div
          v-for="p in state.participants"
          :key="p.identity"
          class="participant-tile"
          :ref="el => setParticipantRef(el, p.identity)"
        >
          <div class="participant-label">{{ p.identity }}</div>
        </div>

        <div v-if="state.participants.length === 0" class="waiting-tile">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" style="color:rgba(255,255,255,0.4)"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
          <p class="mt-2" style="color:rgba(255,255,255,0.5)">Waiting for your practitioner…</p>
        </div>
      </div>

      <!-- Self video PiP -->
      <div class="self-pip" ref="selfVideoContainer">
        <div class="self-label">You</div>
      </div>

      <!-- Controls toolbar -->
      <div class="toolbar">
        <button
          class="ctrl-btn"
          :class="{ 'ctrl-btn--active': state.isMuted }"
          @click="toggleMute"
          :title="state.isMuted ? 'Unmute' : 'Mute'"
        >
          <svg v-if="!state.isMuted" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3 3 3 0 0 1-3-3V5a3 3 0 0 1 3-3m7 9c0 3.53-2.61 6.44-6 6.93V21h-2v-3.07C7.61 17.44 5 14.53 5 11h2a5 5 0 0 0 5 5 5 5 0 0 0 5-5h2z"/></svg>
          <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28m-4.02.17c0-.06.02-.11.02-.17V5a3 3 0 0 0-3-3 3 3 0 0 0-3 3v.18l5.98 5.99M4.27 3 3 4.27l6.01 6.01V11a3 3 0 0 0 3 3c.23 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"/></svg>
        </button>
        <button
          class="ctrl-btn"
          :class="{ 'ctrl-btn--active': state.isVideoOff }"
          @click="toggleVideo"
          :title="state.isVideoOff ? 'Start video' : 'Stop video'"
        >
          <svg v-if="!state.isVideoOff" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/></svg>
          <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M21 6.5l-4 4V7c0-.55-.45-1-1-1H9.82L21 17.18V6.5zM3.27 2 2 3.27l4.73 4.73H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.21 0 .39-.08.54-.18L19.73 21 21 19.73 3.27 2z"/></svg>
        </button>
        <button
          class="ctrl-btn ctrl-btn--hangup"
          @click="leaveRoom"
          title="Leave session"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z" transform="rotate(135 12 12)"/></svg>
        </button>
      </div>
    </div>

    <!-- Post-session -->
    <div v-else-if="state.ended" class="state-screen">
      <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor" class="icon-success"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm4.59-12.42L10 14.17l-2.59-2.58L6 13l4 4 8-8-1.41-1.42z"/></svg>
      <h3 class="mt-3">Session ended</h3>
      <p class="text-medium-emphasis mt-1">Thank you for attending your appointment.</p>
      <v-btn class="mt-6" color="primary" variant="outlined" @click="$router.back()">Back to appointments</v-btn>
    </div>

  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, onMounted, onUnmounted, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { connect, LocalVideoTrack } from 'twilio-video';
import { VideoService } from '@/api/services/video';
import moment from 'moment';

const route = useRoute();
const router = useRouter();

const selfVideoContainer = ref<HTMLElement | null>(null);
const previewContainer = ref<HTMLElement | null>(null);
const participantRefs = ref<Record<string, HTMLElement>>({});

const eventId = Number(route.params.eventId);
const videoService = new VideoService(eventId);

type GateStatus = 'loading' | 'too-early' | 'open' | 'too-late';

const state = reactive({
  loading: true,
  joining: false,
  fatalError: null as string | null,
  error: null as string | null,
  event: null as any,
  windowOpens: null as string | null,
  windowCloses: null as string | null,
  gateStatus: 'loading' as GateStatus,
  isConnected: false,
  ended: false,
  participants: [] as { identity: string }[],
  isMuted: false,
  isVideoOff: false,
  countdown: '',
});

let room: any = null;
let countdownTimer: ReturnType<typeof setInterval> | null = null;
let previewTrack: any = null;

// ─────────────────────────────────────────────────────────────
// Lifecycle
// ─────────────────────────────────────────────────────────────

onMounted(async () => {
  await loadEvent();
});

onUnmounted(() => {
  if (room) room.disconnect();
  if (countdownTimer) clearInterval(countdownTimer);
  if (previewTrack) {
    previewTrack.stop();
    previewTrack.detach().forEach((el: HTMLElement) => el.remove());
  }
});

// ─────────────────────────────────────────────────────────────
// Load event & evaluate time gate
// ─────────────────────────────────────────────────────────────

async function loadEvent() {
  state.loading = true;

  try {
    const res = await videoService.getEvent();
    const data = res.data?.data ?? res.data;
    state.event = data;
    state.windowOpens = data.window_opens ?? null;
    state.windowCloses = data.window_closes ?? null;

    evaluateGate();
    startCountdownIfNeeded();
  } catch (e: any) {
    state.fatalError = e?.response?.data?.error ?? 'Failed to load session details.';
  } finally {
    state.loading = false;
  }

  // Must run after loading=false so the lobby DOM (previewContainer ref) has rendered
  await startPreview();
}

function evaluateGate() {
  if (!state.windowOpens || !state.windowCloses) {
    state.gateStatus = 'open';
    return;
  }

  const now = Date.now();
  const opens = new Date(state.windowOpens).getTime();
  const closes = new Date(state.windowCloses).getTime();

  if (now < opens) {
    state.gateStatus = 'too-early';
  } else if (now > closes) {
    state.gateStatus = 'too-late';
  } else {
    state.gateStatus = 'open';
  }
}

function startCountdownIfNeeded() {
  if (state.gateStatus !== 'too-early' || !state.windowOpens) return;

  const tick = () => {
    const diff = new Date(state.windowOpens!).getTime() - Date.now();
    if (diff <= 0) {
      state.gateStatus = 'open';
      clearInterval(countdownTimer!);
      countdownTimer = null;
      startPreview();
      return;
    }
    const m = Math.floor(diff / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    state.countdown = `${m}m ${s.toString().padStart(2, '0')}s`;
  };

  tick();
  countdownTimer = setInterval(tick, 1000);
}

// ─────────────────────────────────────────────────────────────
// Camera preview in lobby
// ─────────────────────────────────────────────────────────────

async function startPreview() {
  if (state.gateStatus !== 'open') return;

  await nextTick();
  if (!previewContainer.value) return;

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    const track = stream.getVideoTracks()[0];
    const video = document.createElement('video');
    video.srcObject = stream;
    video.autoplay = true;
    video.muted = true;
    video.playsInline = true;
    video.style.cssText = 'width:100%;height:100%;object-fit:cover;border-radius:12px;';
    previewContainer.value.innerHTML = '';
    previewContainer.value.appendChild(video);
    previewTrack = { stop: () => track.stop(), detach: () => [] };
  } catch {
    // Camera access denied — preview placeholder stays
  }
}

// ─────────────────────────────────────────────────────────────
// Room management
// ─────────────────────────────────────────────────────────────

async function joinRoom() {
  state.joining = true;
  state.error = null;

  // Stop preview stream before connecting
  if (previewTrack) {
    previewTrack.stop();
    previewTrack = null;
    if (previewContainer.value) previewContainer.value.innerHTML = '';
  }

  try {
    const res = await videoService.getToken();
    const { token, room_name } = res.data ?? res;

    room = await connect(token, {
      name: room_name,
      audio: true,
      video: { width: 1280, height: 720 },
    });

    // Set connected first so Vue renders the room layout (incl. selfVideoContainer)
    state.isConnected = true;
    await nextTick();

    room.localParticipant.tracks.forEach((pub: any) => {
      if (pub.track && selfVideoContainer.value) {
        selfVideoContainer.value.innerHTML = '';
        const el = pub.track.attach();
        el.style.cssText = 'width:100%;height:100%;object-fit:cover;border-radius:8px;';
        selfVideoContainer.value.appendChild(el);
      }
    });

    room.participants.forEach(onParticipantConnected);
    room.on('participantConnected', onParticipantConnected);
    room.on('participantDisconnected', onParticipantDisconnected);
    room.on('disconnected', onRoomDisconnected);
  } catch (e: any) {
    state.error = e?.message ?? 'Failed to join. Please check your camera and microphone permissions.';
  } finally {
    state.joining = false;
  }
}

function setParticipantRef(el: HTMLElement | null, identity: string) {
  if (el) participantRefs.value[identity] = el;
}

function attachTrack(track: any, identity: string) {
  nextTick(() => {
    const wrapper = participantRefs.value[identity];
    if (!wrapper) return;
    if (track.kind === 'video') {
      wrapper.querySelector('video')?.remove();
      const el = track.attach();
      el.style.cssText = 'width:100%;height:100%;object-fit:cover;';
      wrapper.appendChild(el);
    }
  });
}

function onParticipantConnected(participant: any) {
  if (!state.participants.find(p => p.identity === participant.identity)) {
    state.participants.push({ identity: participant.identity });
  }
  participant.tracks.forEach((pub: any) => {
    if (pub.isSubscribed && pub.track) attachTrack(pub.track, participant.identity);
  });
  participant.on('trackSubscribed', (track: any) => attachTrack(track, participant.identity));
  participant.on('trackUnsubscribed', (track: any) => track.detach().forEach((el: HTMLElement) => el.remove()));
}

function onParticipantDisconnected(participant: any) {
  state.participants = state.participants.filter(p => p.identity !== participant.identity);
}

function onRoomDisconnected() {
  state.isConnected = false;
  state.ended = true;
  state.participants = [];
  participantRefs.value = {};
}

function leaveRoom() {
  if (room) room.disconnect();
}

function toggleMute() {
  if (!room) return;
  state.isMuted = !state.isMuted;
  room.localParticipant.audioTracks.forEach(({ track }: any) => track.enable(!state.isMuted));
}

function toggleVideo() {
  if (!room) return;
  state.isVideoOff = !state.isVideoOff;
  room.localParticipant.videoTracks.forEach(({ track }: any) => track.enable(!state.isVideoOff));
}

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

function formatDate(dateStr: string) {
  return moment(dateStr).format('ddd D MMM YYYY, h:mm a');
}
</script>

<style scoped>
/* Shared */
.video-session-page {
  min-height: 100vh;
  background: #0f0f1a;
  display: flex;
  flex-direction: column;
}

.state-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 80vh;
  padding: 32px 24px;
  text-align: center;
  color: #fff;
}

.state-screen h3 {
  font-size: 20px;
  font-weight: 600;
}

/* Countdown */
.countdown {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.countdown__label {
  font-size: 12px;
  color: rgba(255,255,255,0.5);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.countdown__time {
  font-size: 32px;
  font-weight: 700;
  color: #1ebcd5;
  font-variant-numeric: tabular-nums;
}

/* Lobby */
.lobby {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 24px;
  background: #0f0f1a;
}

.lobby-card {
  background: #1c1c2e;
  border-radius: 16px;
  padding: 32px 28px;
  max-width: 420px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: #fff;
}

.preview-container {
  width: 100%;
  height: 200px;
  background: #0f0f1a;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: rgba(255,255,255,0.3);
}

/* Room layout */
.room-layout {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #0f0f1a;
  min-height: 100vh;
}

.participants-grid {
  flex: 1;
  display: grid;
  gap: 8px;
  padding: 12px;
  padding-bottom: 80px;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  overflow: auto;
}

.participants-grid.single {
  grid-template-columns: 1fr;
}

.participant-tile {
  position: relative;
  background: #1c1c2e;
  border-radius: 12px;
  overflow: hidden;
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.participant-label {
  position: absolute;
  bottom: 10px;
  left: 10px;
  background: rgba(0,0,0,0.6);
  color: #fff;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  pointer-events: none;
}

.waiting-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 240px;
  grid-column: 1 / -1;
}

/* Self PiP */
.self-pip {
  position: fixed;
  bottom: 84px;
  right: 16px;
  width: 140px;
  height: 105px;
  background: #1c1c2e;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.5);
  z-index: 10;
}

.self-label {
  position: absolute;
  bottom: 5px;
  left: 7px;
  background: rgba(0,0,0,0.6);
  color: #fff;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  pointer-events: none;
}

/* Toolbar */
.toolbar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 72px;
  background: rgba(15, 15, 26, 0.95);
  backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  z-index: 20;
}

.ctrl-btn {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: none;
  background: rgba(255,255,255,0.12);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.ctrl-btn--active {
  background: rgba(239, 83, 80, 0.25);
  color: #ef5350;
}

.ctrl-btn--hangup {
  background: #ef5350;
  width: 60px;
  height: 60px;
}

.ctrl-btn:hover:not(.ctrl-btn--hangup) {
  background: rgba(255,255,255,0.22);
}

.ctrl-btn--hangup:hover {
  background: #e53935;
}

/* Icon colour helpers */
.icon-primary { color: #1ebcd5; }
.icon-error   { color: #ef5350; }
.icon-warning { color: #fb8c00; }
.icon-success { color: #4caf50; }

.text-error {
  color: #ef5350;
  font-size: 13px;
}

.text-sm {
  font-size: 13px;
}

.font-size-18-600 {
  font-size: 18px;
  font-weight: 600;
  color: #fff;
}
</style>
